import Checkbox from '@alkem/react-ui-checkbox';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { createRef, PureComponent } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import highlightText from '../utils/highlightText';
import { get, has, size } from '../utils/immutable';
import './item.scss';

export const NOT_SELECTED = 0;
export const SELECTED = 1;
export const PARTIALLY_SELECTED = 2;

class ChoiceTreeItem extends PureComponent {
  static defaultProps = {
    extraOptions: {},
    depth: 0,
    selectedPath: [],
    multiple: false,
    noToggleIcon: false,
    parentLabels: [],
    flatten: false,
    matchText: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      isOpen: !!get(props.item, 'isOpen'),
    };
    this.toggle = this.toggle.bind(this);
    this.onCheckboxChange = this.onCheckboxChange.bind(this);
    this.stopPropagation = this.stopPropagation.bind(this);
    this.onClickSelect = this.onClickCompletion.bind(this, true);
    this.onClickUnselect = this.onClickCompletion.bind(this, false);
    this.itemRef = createRef();
  }

  componentDidUpdate(prevProps) {
    this.checkAndOpen(prevProps);
    const { focusPath, selectedPath, multiple } = this.props;
    if (multiple) {
      return;
    }
    if (
      prevProps.focusPath.join() !== focusPath.join() &&
      focusPath.length !== 0
    ) {
      this.ensureActiveItemVisible(focusPath);
    } else if (
      focusPath.length === 0 &&
      prevProps.selectedPath.join() !== selectedPath.join()
    ) {
      this.ensureActiveItemVisible(selectedPath);
    }
  }

  onClickCompletion(select, event) {
    const { item, path, onSelect, extraOptions } = this.props;
    // we just wanna toggle this node => stop the propagation of the click
    event.stopPropagation();
    if (
      this.hasChildren(item) &&
      this.isExpandable(item) &&
      !extraOptions.selectableParent
    ) {
      this.toggle();
    } else {
      // pass the item and the path to that item to the higher component
      onSelect(item, path.split(','), select);
    }
  }

  onClickToggle = (event) => {
    event.stopPropagation();
    this.toggle();
  };

  onCheckboxChange(select) {
    const { item, path, onSelect } = this.props;
    onSelect(item, path.split(','), select);
  }

  getChildrenSelectionStatus(children, path, selectedPath) {
    if (!this.hasChildren({ children })) {
      return null;
    }
    const childrenStatus = children.map((child, i) => {
      const currentPath = path
        .split(',')
        .concat(this.props.selectors.selectId(child, i))
        .join();
      const isSelected = selectedPath.some((p) => p.join() === currentPath);
      if (isSelected) {
        return SELECTED;
      }
      if (this.hasChildren(child)) {
        return this.getChildrenSelectionStatus(
          get(child, 'children'),
          currentPath,
          selectedPath
        );
      }
      return NOT_SELECTED;
    });
    const firstStatus = get(childrenStatus, 0);
    const hasAllSameStatus = childrenStatus.every(
      (status) => status === firstStatus
    );
    if (hasAllSameStatus) {
      return get(childrenStatus, 0);
    }
    return PARTIALLY_SELECTED;
  }

  getSelectionStatus() {
    const { multiple, selectedPath, path, item } = this.props;
    if (multiple) {
      const isSelected = selectedPath.some(
        (p) => path === p.join() || path.indexOf(`${p.join()},`) === 0
      );
      if (isSelected) {
        return SELECTED;
      }
      if (this.hasChildren(item)) {
        return this.getChildrenSelectionStatus(
          get(item, 'children'),
          path,
          selectedPath
        );
      }
      return NOT_SELECTED;
    }
    return selectedPath.join() === path ? SELECTED : NOT_SELECTED;
  }

  isExpandable(item) {
    return has(item, 'isExpandable') ? get(item, 'isExpandable') : true;
  }

  checkAndOpen(prevProps) {
    if (
      get(this.props.item, 'isOpen') !== get(prevProps.item, 'isOpen') &&
      !!get(this.props.item, 'isOpen') !== this.state.isOpen
    ) {
      this.setState({ isOpen: !!get(this.props.item, 'isOpen') });
    }
  }

  ensureActiveItemVisible(activePath) {
    const { path, scrollToItem } = this.props;
    if (path === activePath.join() && this.itemRef.current) {
      scrollToItem(this.itemRef.current);
    }
  }

  toggle() {
    this.setState((prevState) => ({ isOpen: !prevState.isOpen }));
  }

  hasChildren(item) {
    const children = get(item, 'children', []);
    const count = size(children) || 0;
    return count > 0;
  }

  stopPropagation(event) {
    event.stopPropagation();
  }

  renderToggle(item) {
    if (this.props.noToggleIcon) {
      return <span className="ChoiceTreeItem__noToggleIcon" />;
    }
    const iconClasses = {
      ChoiceTreeItem__chevron: true,
      'ChoiceTreeItem__chevron--hidden': !this.hasChildren(item),
      mdi: true,
      'mdi-chevron-down': this.state.isOpen,
      'mdi-chevron-right': !this.state.isOpen,
    };
    return (
      <span className={classNames(iconClasses)} onClick={this.onClickToggle} />
    );
  }

  render() {
    const {
      item,
      focusPath,
      selectedPath,
      extraOptions,
      depth,
      path,
      scrollToItem,
      id,
      multiple,
      selectors,
      onSelect,
      parentLabels,
      flatten,
      matchText,
      renderItem,
    } = this.props;
    const { isOpen } = this.state;
    const label = selectors.selectLabel(item, extraOptions);
    const selectionStatus = this.getSelectionStatus();
    const isSelected = selectionStatus === SELECTED;
    const isPartiallySelected = selectionStatus === PARTIALLY_SELECTED;
    let children = null;
    const newParentLabels = [...(parentLabels || []), label];

    if ((isOpen || flatten) && this.hasChildren(item)) {
      children = get(item, 'children').map((child, i) => {
        const itemId = selectors.selectId(child, i);
        const key = `${id}-${itemId}`;
        return (
          <ChoiceTreeItem
            key={key}
            id={key}
            item={child}
            selectedPath={selectedPath}
            focusPath={focusPath}
            extraOptions={extraOptions}
            onSelect={onSelect}
            depth={depth + 1}
            scrollToItem={scrollToItem}
            multiple={multiple}
            selectors={selectors}
            path={path.split(',').concat(itemId).join()}
            parentLabels={newParentLabels}
            flatten={flatten}
            matchText={matchText}
            renderItem={renderItem}
          />
        );
      });
    }

    const rootClasses = {
      'ChoiceTreeItem--hide': !!get(item, 'hide'),
    };
    if (get(item, 'customClass')) {
      rootClasses[get(item, 'customClass')] = true;
    }
    const itemClasses = {
      ChoiceTreeItem: true,
      'ChoiceTreeItem--selected': isSelected,
      'ChoiceTreeItem--onFocus': !multiple && focusPath.join() === path,
      'ChoiceTreeItem--multiple': multiple,
      'ChoiceTreeItem--partiallySelected': !isSelected && isPartiallySelected,
    };
    const itemLabel = renderItem ? renderItem(label, item) : label;

    // If displaying only leaves, we only render the children.
    if (flatten && this.hasChildren(item)) {
      return (
        <div
          id={id}
          data-testid={item && item.key}
          className={classNames(rootClasses)}
        >
          <div className="ChoiceTreeItem__childrenList">{children}</div>
        </div>
      );
    } else if (flatten) {
      return (
        <div
          id={id}
          data-testid={item && item.key}
          className={classNames(rootClasses)}
        >
          <div className={classNames(itemClasses)} ref={this.itemRef}>
            <div
              onClick={isSelected ? this.onClickUnselect : this.onClickSelect}
              className="ChoiceTreeItem__item"
            >
              <span className="ChoiceTreeItem__itemLabel">
                {highlightText(newParentLabels.join(' > '), matchText, true)}
              </span>
            </div>
          </div>
        </div>
      );
    }

    const spacerWidth = { width: `${depth * 15}px` };
    return (
      <div
        id={id}
        data-testid={item && item.key}
        className={classNames(rootClasses)}
      >
        <div className={classNames(itemClasses)} ref={this.itemRef}>
          <div
            onClick={isSelected ? this.onClickUnselect : this.onClickSelect}
            className="ChoiceTreeItem__item"
          >
            <div className="ChoiceTreeItem__spacer" style={spacerWidth} />
            {this.renderToggle(item)}
            {multiple && (
              <div
                className="ChoiceTreeItem__checkbox"
                onClick={this.stopPropagation}
              >
                <Checkbox
                  id={`ChoiceTreeItem-checkbox-${id}`}
                  onChange={this.onCheckboxChange}
                  checked={isSelected}
                  partiallyChecked={isPartiallySelected}
                />
              </div>
            )}
            <span className="ChoiceTreeItem__itemLabel">{itemLabel}</span>
          </div>
        </div>
        {isOpen && (
          <div className="ChoiceTreeItem__childrenList">{children}</div>
        )}
      </div>
    );
  }
}

ChoiceTreeItem.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onSelect: PropTypes.func.isRequired,
  item: PropTypes.oneOfType([PropTypes.object, ImmutablePropTypes.map])
    .isRequired,
  focusPath: PropTypes.array,
  selectedPath: PropTypes.array,
  extraOptions: PropTypes.object,
  depth: PropTypes.number,
  path: PropTypes.string.isRequired,
  scrollToItem: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
  noToggleIcon: PropTypes.bool,
  parentLabels: PropTypes.array,
  flatten: PropTypes.bool,
  matchText: PropTypes.string,
  selectors: PropTypes.shape({
    selectId: PropTypes.func.isRequired,
    selectLabel: PropTypes.func.isRequired,
  }).isRequired,
  renderItem: PropTypes.func,
};

export default ChoiceTreeItem;
